import type { XmlItemRating } from "../../../shared/analysis-types";
import {
  SECTION_DISPLAY_NAMES,
  SECTION_NAMES,
  type SectionName,
} from "../../../shared/analysis-types";
import type {
  ScorecardWorkbenchBaseline,
  ScorecardWorkbenchSection,
  ScorecardWorkbenchUsage,
} from "../../../shared/scorecard-workbench";
import { scorecardRatingDisplay } from "./analysis-display";

const RATING_ORDER: readonly XmlItemRating[] = [
  "exceeds",
  "great",
  "meets",
  "not_yet",
  "needs_improvement",
  "na",
  "unknown",
];

/** The one row shape the fixture and production section markdown share. */
const ITEM_ROW = /^-\s+\*\*(?<id>[a-z0-9_]+)\*\*\s+—\s+(?<rating>[a-z_]+)\b/gim;

export interface ScorecardRatingCount {
  rating: XmlItemRating;
  label: string;
  count: number;
}

export interface ScorecardSectionTally {
  name: string;
  title: string;
  itemCount: number;
  catalogCount: number;
  counts: ScorecardRatingCount[];
}

export interface BaselineFacts {
  modelKey: string;
  analysisVersion: string;
  elapsed: string;
  usage: ScorecardWorkbenchUsage | null;
}

export interface BaselineSummary {
  sessionId: string;
  sections: ScorecardSectionTally[];
  sectionsReceived: number;
  facts: BaselineFacts;
}

function isRating(value: string): value is XmlItemRating {
  return (RATING_ORDER as readonly string[]).includes(value);
}

function ratingsIn(section: ScorecardWorkbenchSection): XmlItemRating[] {
  const ratings: XmlItemRating[] = [];

  for (const match of section.markdown.matchAll(ITEM_ROW)) {
    const rating = match.groups?.rating ?? "";
    ratings.push(isRating(rating) ? rating : "unknown");
  }

  return ratings;
}

function tallyOf(section: ScorecardWorkbenchSection): ScorecardSectionTally {
  const ratings = ratingsIn(section);
  const counts = RATING_ORDER.map((rating) => ({
    rating,
    label: scorecardRatingDisplay[rating].label,
    count: ratings.filter((value) => value === rating).length,
  })).filter((entry) => entry.count > 0);

  return {
    name: section.name,
    title: SECTION_DISPLAY_NAMES[section.name as SectionName] ?? section.title,
    itemCount: ratings.length,
    catalogCount: section.itemIds.length,
    counts,
  };
}

/** Scorecard sections are the rated ones; narrative sections carry no items. */
export function tallySections(
  sections: readonly ScorecardWorkbenchSection[]
): ScorecardSectionTally[] {
  return sections.map(tallyOf).filter((tally) => tally.itemCount > 0);
}

export function elapsedBetween(createdAt: string, completedAt: string | null): string {
  if (!completedAt) return "—";

  const ms = Date.parse(completedAt) - Date.parse(createdAt);
  if (!Number.isFinite(ms) || ms < 0) return "—";

  const totalSeconds = Math.round(ms / 1_000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

/** A section that rated fewer items than its catalog carries is incomplete. */
function missingCount(sections: readonly ScorecardSectionTally[]): number {
  return sections.reduce(
    (total, section) => total + Math.max(0, section.catalogCount - section.itemCount),
    0
  );
}

function unknownCount(sections: readonly ScorecardSectionTally[]): number {
  return sections.reduce(
    (total, section) =>
      total + (section.counts.find((entry) => entry.rating === "unknown")?.count ?? 0),
    0
  );
}

function formatVerdict(unknown: number, missing: number): string {
  if (missing > 0) {
    return `Format OK, ${missing} item${missing === 1 ? "" : "s"} missing.`;
  }

  if (unknown > 0) return "Format OK, unknown ratings.";

  return "Format OK.";
}

/**
 * Deterministic: it counts what arrived and never judges the content. The
 * reader learns whether the Scorecard is whole before reading any of it.
 */
export function baselineFormatLine(
  sections: readonly ScorecardSectionTally[],
  sectionsReceived: number
): string {
  if (!sections.length) return "No rated sections in this Scorecard.";

  const unknown = unknownCount(sections);
  const missing = missingCount(sections);
  const rated = sections
    .map((section) => `${section.itemCount} ${section.title}`)
    .join(" and ");
  const ratings =
    unknown === 0
      ? "no unknown ratings"
      : `${unknown} unknown ${unknown === 1 ? "rating" : "ratings"}`;
  const coverage = missing === 0 ? `all ${rated}` : rated;
  const verdict = formatVerdict(unknown, missing);

  return `${verdict} ${sectionsReceived} of ${SECTION_NAMES.length} sections · ${coverage} items rated · ${ratings}.`;
}

export function summarizeBaseline(
  baseline: ScorecardWorkbenchBaseline
): BaselineSummary | null {
  if (baseline.status !== "ready") return null;

  return {
    sessionId: baseline.sessionId,
    sections: tallySections(baseline.sections),
    sectionsReceived: baseline.sections.length,
    facts: {
      modelKey: baseline.revision.modelKey,
      analysisVersion: baseline.revision.analysisVersion,
      elapsed: elapsedBetween(
        baseline.revision.createdAt,
        baseline.revision.completedAt
      ),
      usage: baseline.usage,
    },
  };
}
